/**
 * Skin Analysis (P1) — domain entities, value objects, enums, constants.
 *
 * Source of truth: "Skin Analysis P1 API Specification" (2026-08-01), which the backend
 * ships. Report (API 8) and Recommendations (API 9) are now fully specified, so the fields
 * this file previously carried as PROVISIONAL guesses are gone — `peerAverage` became
 * `metrics[].comparison`, `additionalImageUrls` became `faceImage.alignedFaceUrl`, and report 2's
 * `concerns`/`milestones`/`isKeyStep`/`concentration` were replaced by `priorityConcerns`,
 * the report's own `expectedChanges`, and `validationFacts`. HTTP wire contracts live in
 * infrastructure/skinAnalysis/utils/types.ts (which alias these entities).
 */

import type { Currency } from '../../constants/currency/currency';
import type { SimpleProduct } from '../product/product';

export type SkinAnalysisImageRole = 'front' | 'left' | 'right';

/**
 * Upload MIME type (presigned PUT Content-Type). The backend accepts jpeg/png/webp/heic/heif,
 * but the app only ever captures + sends JPEG (the capture flow converts to JPEG), so the
 * app-side contract is narrowed to `image/jpeg`.
 */
export type SkinAnalysisContentType = 'image/jpeg';

/**
 * Top-level state from `GET /skinAnalysis/me/status` (contract API 1) — tells the
 * app which screen to show on entry.
 */
export type SkinAnalysisStatus =
  | 'survey_required'
  | 'ready_to_capture'
  | 'analysis_processing'
  | 'completed'
  | 'failed'
  | 'expired';

/**
 * Per-result lifecycle status (contract API 1 `activeResult.status` and API 7 poll).
 * Exactly the six contract values; terminal: completed, failed, expired. Image-quality
 * failure is NOT a status — `POST /results` returns it synchronously (see
 * SkinAnalysisImageQualityError), so there is no `retake_required` poll state.
 */
export type SkinAnalysisResultStatus = 'queued' | 'processing' | 'vendor_polling' | 'completed' | 'failed' | 'expired';

/**
 * The non-terminal statuses — what "an analysis is still in flight" means.
 *
 * This is the set the server calls ACTIVE: it decides whether a new `POST /results` is allowed,
 * and it is the only thing a create response or an `active_result_exists` conflict can report.
 */
export type SkinAnalysisInFlightStatus = Extract<SkinAnalysisResultStatus, 'queued' | 'processing' | 'vendor_polling'>;

const SKIN_ANALYSIS_IN_FLIGHT_STATUSES: readonly SkinAnalysisInFlightStatus[] = [
  'queued',
  'processing',
  'vendor_polling',
];

export function isSkinAnalysisInFlightStatus(value: unknown): value is SkinAnalysisInFlightStatus {
  return SKIN_ANALYSIS_IN_FLIGHT_STATUSES.includes(value as SkinAnalysisInFlightStatus);
}

/**
 * Progressive result readiness (Facade PR #1026): the report and the product
 * recommendations publish independently, and the report lands FIRST — often while
 * the overall `status` is still in flight. `failed` is terminal for the section:
 * the overall result reached a terminal status without that section's payload.
 */
export type SkinAnalysisResultSectionStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface SkinAnalysisResultSectionProgress {
  status: SkinAnalysisResultSectionStatus;
  /** Present only once the section is completed. */
  completedAt?: string;
}

export interface SkinAnalysisResultSections {
  report: SkinAnalysisResultSectionProgress;
  recommendations: SkinAnalysisResultSectionProgress;
}

/** The slice of the status response the section-readiness checks below read. */
interface SkinAnalysisSectionedStatus {
  status: SkinAnalysisResultStatus;
  sections?: SkinAnalysisResultSections | null;
}

/**
 * Whether `GET /report` can be read for this result. On the progressive contract
 * this flips while the overall status is still in flight; a backend without
 * `sections` (or a legacy record) only reaches it at overall completion.
 */
export function isSkinAnalysisReportReady(result: SkinAnalysisSectionedStatus | undefined): boolean {
  if (result == null) {
    return false;
  }
  if (result.sections != null) {
    return result.sections.report.status === 'completed';
  }
  return result.status === 'completed';
}

/** Whether `GET /recommendations` stopped returning 409 — the routine CTA gates on this. */
export function isSkinAnalysisRecommendationsReady(result: SkinAnalysisSectionedStatus | undefined): boolean {
  if (result == null) {
    return false;
  }
  if (result.sections != null) {
    return result.sections.recommendations.status === 'completed';
  }
  return result.status === 'completed';
}

/** The 9 P1 score-bearing metrics (contract: metrics list is exactly these). */
export type SkinAnalysisMetricKey =
  | 'breakouts'
  | 'pores'
  | 'redness'
  | 'pigmentation'
  | 'lines'
  | 'sagging'
  | 'uniformness'
  | 'dark_circles'
  | 'puffiness';

export type SkinAnalysisSurveyQuestionType = 'single' | 'multi' | 'ranked_multi';

export type SkinAnalysisRoutineScheduleTag = 'AM' | 'PM';

/** Whether a saved location was returned (`user_selected`) or one inferred from IP
 *  without saving (`ip_inferred`). The inferred value is INFERRED — confirm with backend. */
export type SkinAnalysisLocationSource = 'user_selected' | 'ip_inferred';

// ----- API 1: me/status -----

export interface SkinAnalysisSurveyResponseRef {
  surveyResponseId: string;
  submittedAt: string;
}

/** Points at one analysis result — the parameter shape for result-scoped reads
 *  (status, report, recommendations). */
export interface SkinAnalysisResultRef {
  resultId: string;
}

/** Result lifecycle pointer (backend `SkinAnalysisResultSummary`) — shared by both
 *  `activeResult` and `latestCompletedResult`. */
export interface SkinAnalysisResultSummary {
  resultId: string;
  status: SkinAnalysisResultStatus;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
}

/**
 * `GET /skinAnalysis/me/status` entity. `activeResult` and `latestCompletedResult`
 * can coexist: a new analysis processing over an old completed result reports
 * top-level `analysis_processing`, with the completed one still in
 * `latestCompletedResult`.
 */
export interface SkinAnalysisStatusOverview {
  status: SkinAnalysisStatus;
  surveyResponse: SkinAnalysisSurveyResponseRef | null;
  activeResult: SkinAnalysisResultSummary | null;
  latestCompletedResult: SkinAnalysisResultSummary | null;
  /** Whether the user may create one more report after seeing a completed one. */
  canCreateAdditionalReport: boolean;
}

// ----- API 2: survey/active -----

export interface SkinAnalysisSurveyOption {
  optionId: string;
  value: string;
  label: string;
  description?: string;
  /**
   * Server-curated artwork for this option, resolved from its `value` against the backend's
   * icon catalog. A remote SVG URL, NOT one of the funnel's named DuotoneIcon ids.
   */
  iconUrl?: string;
  /** Selecting this clears the other selected options in the same question (e.g. "None of the above"). */
  clearsOtherOptions?: boolean;
  /**
   * Retired values and other raw forms the server still accepts for this option.
   *
   * Read-only for the app: a submit always sends `value`. They exist so a response captured
   * under an older wording still resolves to this option when the server replays it back.
   */
  aliases?: string[];
}

export interface SkinAnalysisSurveyQuestion {
  questionId: string;
  title: string;
  description?: string;
  type: SkinAnalysisSurveyQuestionType;
  required: boolean;
  minSelections?: number;
  maxSelections?: number;
  /**
   * Restricts this question's valid answers to the values chosen for an earlier question —
   * the server's own dependency link, so the options are narrowed rather than re-asked.
   */
  optionsFromQuestionId?: string;
  options: SkinAnalysisSurveyOption[];
}

/** Active onboarding survey config. `surveyId` is not exposed to the app; `surveyVersion` is. */
export interface SkinAnalysisSurveyConfig {
  /**
   * The version these questions came from. Send it back on submit: omitting it makes the
   * server infer the version from the question ids, and `12q-v1` and `15q-v1` both use
   * slug ids, so inference has two candidates to choose between.
   */
  surveyVersion: string;
  title: string;
  questions: SkinAnalysisSurveyQuestion[];
}

/** Parameters for the active-survey read — `currency` prices the budget question's
 *  labels ("Under $30" / "Under S$40" / …); omitted, the server prices them in USD. */
export interface SkinAnalysisSurveyQuery {
  currency?: Currency;
}

// ----- GET /skinAnalysis/me/survey — the submitted survey, read-only -----

/** One option the customer picked, with its label restored from the definition they answered. */
export interface SkinAnalysisSavedSurveySelection {
  value: string;
  label: string;
  /** Null for options the icon catalog has no artwork for. */
  iconUrl: string | null;
}

/** One answered question. An unanswered optional question carries an empty `selections`. */
export interface SkinAnalysisSavedSurveyAnswer {
  questionId: string;
  questionTitle: string;
  questionDescription: string | null;
  /** Display order within the survey — the server's, not the order the answers were stored in. */
  order: number;
  type: SkinAnalysisSurveyQuestionType;
  selections: SkinAnalysisSavedSurveySelection[];
}

/**
 * A submitted survey rendered back to the customer, READ-ONLY.
 *
 * Responses store option identifiers only, so the server joins every label from the definition
 * named by `surveyVersion`. That is what keeps an old submission readable after the active
 * survey moves on — and it is why the client prints these strings verbatim: mapping a
 * `questionId` or reinterpreting an option `value` here would resurrect the wording problem the
 * server-side join exists to solve.
 */
export interface SkinAnalysisSavedSurvey {
  surveyResponseId: string;
  surveyVersion: string;
  surveyTitle: string;
  submittedAt: string;
  /**
   * Whether the stored answers may be edited. Currently ALWAYS false: a second scan reuses the
   * first survey rather than re-asking it, so this screen restores a submission and never
   * offers a way to change it. Modelled as a boolean rather than dropped, because the day it
   * can be true the UI must gate on the server's answer instead of a client assumption.
   */
  editable: boolean;
  answers: SkinAnalysisSavedSurveyAnswer[];
}

// ----- API 4: location preference -----

/**
 * The resolved location object (contract API 4 — the `location` field of the
 * preference envelope). `locationId` is a deterministic key like `SG:singapore`.
 */
export interface SkinAnalysisLocation {
  locationId: string;
  countryCode: string;
  countryName: string;
  city: string;
  region?: string | null;
  displayName: string;
}

/**
 * `GET/PUT /skinAnalysis/me/location` envelope (backend API 4): the resolved
 * `location` plus how it was obtained (`source`) and whether it was saved (`persisted`).
 */
export interface SkinAnalysisLocationPreference {
  source: SkinAnalysisLocationSource;
  persisted: boolean;
  location: SkinAnalysisLocation;
}

// ----- live environment for the current location -----

/** Customer-visible air-quality category the server derives from the U.S. AQI scale. */
export type SkinAnalysisAirQualityLevel =
  | 'good'
  | 'moderate'
  | 'unhealthy_for_sensitive_groups'
  | 'unhealthy'
  | 'very_unhealthy'
  | 'hazardous';

/** Air-quality measurement; `level` is server-derived and never caller-authored. */
export interface SkinAnalysisAirQualityMeasurement {
  index: number;
  scale: 'us_aqi';
  level: SkinAnalysisAirQualityLevel;
  pm25MicrogramsM3?: number;
  pm10MicrogramsM3?: number;
}

/** Meteorological season derived from the snapshot date and hemisphere. */
export type SkinAnalysisSeason = 'spring' | 'summer' | 'autumn' | 'winter';

/**
 * Weather and air-quality averages over the observation window. Every measurement is
 * optional: the provider can answer for some and not others, and a consumer must be
 * able to show one metric while the rest are missing.
 */
export interface SkinAnalysisEnvironmentAverage {
  temperatureC?: number;
  humidityPercent?: number;
  uvIndex?: number;
  airQuality?: SkinAnalysisAirQualityMeasurement;
}

/**
 * `GET /skinAnalysis/me/environment` — live climate for the caller's current location,
 * with no analysis result required.
 *
 * The server answers 503 `environment_unavailable` when no measurement is usable at all,
 * and 409 when it cannot resolve a location, so a successful response always carries at
 * least one measurement.
 */
export interface SkinAnalysisEnvironment {
  location: SkinAnalysisLocation;
  windowDays: number;
  season?: SkinAnalysisSeason;
  average: SkinAnalysisEnvironmentAverage;
  /** One English sentence grounded only in the available facts; null when generation failed. */
  guidanceText: string | null;
}

// ----- API 5: image-upload-url -----

export interface SkinAnalysisImageUploadTarget {
  /** Echo of the requested role (contract API 5). */
  role: SkinAnalysisImageRole;
  method: 'PUT';
  url: string;
  /** users/{userId}/skin-analysis/{role}/{uuid}.{ext} — echoed back on POST /results. */
  objectKey: string;
  /** MUST be echoed as the PUT Content-Type header — it is part of the S3 signature. */
  contentType: string;
  expiresAt: string;
  maxBytes: number;
}

// ----- API 6/7: results + status -----

/**
 * One per-image quality problem in a `POST /results` quality-failure response
 * (backend API 6 — the 400 body’s `qualityIssues[]`).
 */
export interface SkinAnalysisQualityIssue {
  role?: SkinAnalysisImageRole;
  code: string;
  message: string;
}

/**
 * Failure payload the poll status can carry (contract API 7 `failure`). When
 * `retakeRequired` is true the app routes to retake-all-three; otherwise it is a
 * terminal error. `issues`/`qualityIssues` are aliases (backend sends both).
 */
export interface SkinAnalysisResultFailure {
  code: string;
  message: string;
  retakeRequired?: boolean;
  qualityIssues?: SkinAnalysisQualityIssue[];
  issues?: SkinAnalysisQualityIssue[];
  reasons?: SkinAnalysisQualityIssue[];
  qualityScore?: number | null;
}

/**
 * Thrown by `createResult` when `POST /results` rejects the photos for quality
 * (contract API 6 — `image_quality_failed`). Carries the per-image issues so the
 * Analysing pipeline can route to the retake screen. The user must retake all three.
 */
export class SkinAnalysisImageQualityError extends Error {
  readonly code = 'image_quality_failed';
  readonly issues: SkinAnalysisQualityIssue[];

  constructor(message: string, issues: SkinAnalysisQualityIssue[]) {
    super(message);
    this.name = 'SkinAnalysisImageQualityError';
    this.issues = issues;
  }
}

/**
 * Thrown by `createResult` when the caller already has an analysis in flight (409
 * `active_result_exists`).
 *
 * A resubmission of the SAME object keys and currency does not land here at all — the server
 * answers it idempotently with the running result. But object keys are minted per presign and
 * carry a timestamp, so anything that re-uploads produces a submission the server cannot
 * recognise, and gets this instead.
 *
 * `resultId` and `status` name the analysis already running. They are relayed because the
 * server sends them, not because the app currently routes on them: a conflict takes the same
 * recovery as every other server failure (decision 2026-08-12 — one failure flow, not two).
 * Both are nullable because the server omits them on one path: when two submissions race for
 * the active-result claim, the loser is told a result exists without being told which.
 */
export class SkinAnalysisActiveResultError extends Error {
  readonly code = 'active_result_exists';
  readonly resultId: string | null;
  readonly status: SkinAnalysisInFlightStatus | null;

  constructor(message: string, resultId: string | null, status: SkinAnalysisInFlightStatus | null) {
    super(message);
    this.name = 'SkinAnalysisActiveResultError';
    this.resultId = resultId;
    this.status = status;
  }
}

/**
 * Thrown by `createResult` when the account has used up its completed reports (409
 * `report_limit_reached`). The counts come from the server so the screen states the real limit
 * rather than repeating a number the client believes.
 */
export class SkinAnalysisReportLimitError extends Error {
  readonly code = 'report_limit_reached';
  readonly completedReportCount: number;
  readonly maxCompletedReportCount: number;

  constructor(message: string, completedReportCount: number, maxCompletedReportCount: number) {
    super(message);
    this.name = 'SkinAnalysisReportLimitError';
    this.completedReportCount = completedReportCount;
    this.maxCompletedReportCount = maxCompletedReportCount;
  }
}

/**
 * Thrown by `createResult` when no survey response is on file yet (409 `survey_required`).
 * The scan cannot be paired with answers that were never submitted, so the flow belongs back
 * at the questionnaire rather than at the camera.
 */
export class SkinAnalysisSurveyRequiredError extends Error {
  readonly code = 'survey_required';

  constructor(message: string) {
    super(message);
    this.name = 'SkinAnalysisSurveyRequiredError';
  }
}

// ----- API 9: recommendations -----

// ----- Haut runs: survey-independent image analysis (SSD-6097) -----

/**
 * Provider status of a Haut run. `passed` and `failed` are terminal; `expired` means the
 * server's 60-minute TTL elapsed before the run was bound to a survey response.
 */
export type SkinAnalysisHautRunStatus = 'queued' | 'processing' | 'passed' | 'failed' | 'expired';

/** `POST /hautRuns` — the run the photos were handed to, before any survey exists for it. */
export interface SkinAnalysisHautRunStartResponse {
  hautRunId: string;
  status: SkinAnalysisHautRunStatus;
}

/**
 * Finalization conflicts preserve their wire code: unavailable/expired runs need new
 * photos, while an already-bound run must not silently change its survey binding.
 */
export class SkinAnalysisHautRunUnavailableError extends Error {
  constructor(
    message: string,
    readonly code: 'haut_run_unavailable' | 'haut_run_expired' | 'haut_run_already_bound' = 'haut_run_unavailable',
  ) {
    super(message);
    this.name = 'SkinAnalysisHautRunUnavailableError';
  }
}

/**
 * Thrown by `getRecommendations` when the routine cannot be read yet (409 `result_not_ready`).
 *
 * `sectionStatus` is the server's own verdict on the recommendations section, and it is the
 * whole reason this error is typed: `failed` means the result settled without a routine and no
 * amount of retrying produces one, while every other value means it is still on its way. The
 * screen promises different things in those two cases.
 */
export class SkinAnalysisRecommendationsNotReadyError extends Error {
  readonly code = 'result_not_ready';
  readonly sectionStatus: SkinAnalysisResultSectionStatus | null;

  constructor(message: string, sectionStatus: SkinAnalysisResultSectionStatus | null) {
    super(message);
    this.name = 'SkinAnalysisRecommendationsNotReadyError';
    this.sectionStatus = sectionStatus;
  }
}

/**
 * `503 skin_analysis_recommendation_products_unavailable` from the live product reads (SSD-6340):
 * stored recommendation, catalog or FX data could not be read safely. Typed so that no caller can
 * mistake it for an empty or sold-out list — the contract forbids exactly that reading — and so
 * the screen can offer a retry instead of a verdict.
 */
export class SkinAnalysisRecommendationProductsUnavailableError extends Error {
  readonly code = 'skin_analysis_recommendation_products_unavailable';

  constructor(message: string) {
    super(message);
    this.name = 'SkinAnalysisRecommendationProductsUnavailableError';
  }
}

/**
 * A `404` from the product DETAIL read: the occurrence is no longer purchasable (it went out of
 * stock or was unpublished after the list was read). Distinct from the list's own 404, which is a
 * missing or foreign result and stays a plain rejection.
 */
export class SkinAnalysisRecommendationProductGoneError extends Error {
  readonly code = 'recommendation_product_gone';

  constructor(message: string) {
    super(message);
    this.name = 'SkinAnalysisRecommendationProductGoneError';
  }
}

const SKIN_ANALYSIS_SECTION_STATUSES: readonly SkinAnalysisResultSectionStatus[] = [
  'pending',
  'processing',
  'completed',
  'failed',
];

export function isSkinAnalysisResultSectionStatus(value: unknown): value is SkinAnalysisResultSectionStatus {
  return SKIN_ANALYSIS_SECTION_STATUSES.includes(value as SkinAnalysisResultSectionStatus);
}

/**
 * Whether a rejected routine query can never succeed, so the screen must not offer a retry.
 *
 * Only a section the server calls `failed` qualifies. Every other rejection — a still-running
 * section, a network blip, a 5xx — is left retryable: guessing wrong in that direction costs a
 * wasted tap, while guessing wrong the other way strands the reader on a button that cannot work.
 */
export function isSkinAnalysisRoutineTerminal(error: unknown): boolean {
  return error instanceof SkinAnalysisRecommendationsNotReadyError && error.sectionStatus === 'failed';
}

// ----- API 8: report -----

/** Survey-owned age bands the metric benchmark supports (backend SKIN_ANALYSIS_AGE_BANDS). */
export type SkinAnalysisAgeBand = 'under-25' | '25-34' | '35-44' | '45-60' | 'over-60';

/** Age band paired with the customer-facing label the backend renders (e.g. `'25 – 34'`). */
export interface SkinAnalysisAgeBandSummary {
  value: SkinAnalysisAgeBand;
  label: string;
}

/**
 * Customer-facing relative state the backend derives from `score - benchmarkScore`
 * (`<= -5` focus_area, `>= 5` ahead, otherwise on_par).
 */
export type SkinAnalysisMetricRelativeStatus = 'focus_area' | 'on_par' | 'ahead';

export interface SkinAnalysisReportSummary {
  title: string;
  description: string;
  /** Appears only in the summary — skinType has no numeric metric. */
  skinType: string;
  skinAge?: number;
  /**
   * Resolved active-survey age band, omitted when the survey carried no supported age answer.
   * Its `label` is the prefix the report's peer line prints.
   */
  ageBand?: SkinAnalysisAgeBandSummary;
  conditionScore: number;
}

/**
 * The report's scan photo — the aligned FRONT shot, and only that.
 *
 * Three angles are captured, but the report shows one (confirmed by the backend 2026-08-05: the
 * `views` carousel briefly present in the dev fixture was a fixture bug and has been removed).
 * A different image axis from `metrics[].imageUrl`, which is the selected metric's own overlay.
 */
export interface SkinAnalysisFaceImage {
  alignedFaceUrl: string;
}

export interface SkinAnalysisReportMetricRange {
  min: number;
  max: number;
}

export type SkinAnalysisMetricDetailItemDisplayType = 'bullets' | 'tags';

/** One flexible section within a metric's detail block (contract: details.sections[]). */
export interface SkinAnalysisMetricDetailSection {
  sectionId: string;
  title: string;
  description?: string;
  /** Rendered as bullet lines or tag chips per `itemDisplayType`. */
  items: string[];
  itemDisplayType: SkinAnalysisMetricDetailItemDisplayType;
}

export interface SkinAnalysisReportMetricDetails {
  sections: SkinAnalysisMetricDetailSection[];
}

/**
 * How this metric compares to the user's peer cohort (contract API 8 `comparison`).
 *
 * `relativeStatus` is the SERVER's band decision — the client renders it and never
 * re-derives one from `deltaPoints` (the documented provisional policy is
 * `deltaPoints <= -5` → focus_area, `>= 5` → ahead, else on_par, but that is the
 * server's rule to change, not ours).
 */
export interface SkinAnalysisMetricComparison {
  ageBand: SkinAnalysisAgeBand;
  ageBandLabel: string;
  /** 0–100 peer benchmark — the number the report prints as "Ages 25–34 average · 62". */
  benchmarkScore: number;
  deltaPoints: number;
  relativeStatus: SkinAnalysisMetricRelativeStatus;
  benchmarkVersion: string;
  policyVersion: string;
}

export interface SkinAnalysisReportMetric {
  metricKey: SkinAnalysisMetricKey;
  label: string;
  /** Scale 0–100; higher is better. */
  score: number;
  /**
   * One-based rank across the canonical metrics counting from the LOWEST score — rank 1 is the
   * strongest focus area, not the best score. Metric key breaks ties.
   */
  scoreRank?: number;
  /**
   * Age-band comparison, omitted when the survey carried no supported age answer or the server
   * has no benchmark for this metric — the peer UI hides rather than faking one.
   */
  comparison?: SkinAnalysisMetricComparison;
  /** Provider grade (normalized band), preserved separately from the 0–100 score. */
  grade: number;
  range: SkinAnalysisReportMetricRange;
  title: string;
  description: string;
  /** Server-authored sentence for this metric's current state; the line hides when absent. */
  statusHeadline?: string;
  /**
   * Server-authored explanation of the code-owned age-band comparison. `null` when no valid
   * benchmark exists. Never recalculated or reworded on the client.
   */
  benchmarkNarrative?: string | null;
  /**
   * Remote icon for this metric — an SVG the server hosts, sent for all nine metrics.
   *
   * Distinct from `maskUrl` below: this is the small glyph beside the metric's NAME, while the
   * mask is drawn over the face. Optional because responses predating the icons are still in
   * flight; the slot keeps its placeholder square until one arrives.
   */
  iconUrl?: string;
  /**
   * A transparent overlay marking where THIS metric was measured on the face — pores, redness,
   * pigmentation and so on. It is drawn over `faceImage.alignedFaceUrl`, which it is aligned to,
   * and is meaningless on its own: alone it is a floating shape with no face under it.
   */
  maskUrl?: string;
  /**
   * DEPRECATED (backend, 2026-08-12). It now carries the base face for almost every metric, so
   * swapping it in showed the same photo under every trait and lost the analysis entirely. The
   * mask above is what actually differs per metric.
   */
  imageUrl?: string;
  details: SkinAnalysisReportMetricDetails;
}

/** Server-owned timing window identity (SSD-6340) — never rebuilt from array position. */
export type SkinAnalysisExpectedChangePeriod = '2_weeks' | '4_weeks' | '8_to_12_weeks';

/**
 * LLM-authored copy for one client-owned timing-window row, plus the server-owned facts
 * (SSD-6340) that tie it to a concern: `concernKey` is the join key against `priorityConcerns[]`
 * — entries and concerns are independent arrays and must never be paired by index.
 *
 * `period`/`concernKey`/`concernLabel` are one association, present together or not at all — a
 * legacy entry (predating SSD-6340) omits all three but still sends `keyIngredients: []`. Per the
 * contract, the client must not infer a missing association on its own.
 */
export interface SkinAnalysisExpectedChangeEntry {
  period?: SkinAnalysisExpectedChangePeriod;
  concernKey?: string;
  concernLabel?: string;
  title: string;
  description: string;
  /** Up to two code-owned AM/PM active names for this concern, already deduplicated. */
  keyIngredients: string[];
}

export interface SkinAnalysisExpectedChanges {
  /**
   * Optional because it is NEW: `expectedChanges` is stored with the result at generation time
   * and not rebuilt on read, so every result generated before this field shipped answers without
   * one. The timing screen falls back to its own copy rather than losing the section.
   */
  subtitle?: string;
  entries: SkinAnalysisExpectedChangeEntry[];
}

export interface SkinAnalysisReport {
  resultId: string;
  status: 'completed';
  summary: SkinAnalysisReportSummary;
  faceImage: SkinAnalysisFaceImage;
  /** Exactly the 9 score-bearing metrics. */
  metrics: SkinAnalysisReportMetric[];
  expectedChanges?: SkinAnalysisExpectedChanges;
  createdAt: string;
  completedAt: string;
}

// ----- API 9: recommendations -----

/** Tone of an alternative `whyPicked` phrasing (contract API 9). */
export type SkinAnalysisWhyPickedTone = 'evidence' | 'evidence_emotional';

/** Where a step sits in the routine's shape (contract API 9 `RoutinePhase`). */
export type SkinAnalysisRoutinePhase = 'cleanse' | 'prepare' | 'treat' | 'eye_care' | 'moisturize' | 'protect';

/** When a step is applied — one schedule, or `ALL` for a step used both morning and night. */
export type SkinAnalysisRoutineUsage = SkinAnalysisRoutineScheduleTag | 'ALL';

/**
 * The server's own application order for one schedule (contract API 9 `routineSequences`).
 *
 * This is the canonical AM/PM ordering. A step used morning AND night appears in both lists, so
 * the two sequences together describe the routine the customer actually follows — which is not
 * derivable by filtering `steps` on `scheduleTags`, because that only recovers membership, never
 * the order the server chose.
 */
export interface SkinAnalysisRoutineSequence {
  schedule: SkinAnalysisRoutineScheduleTag;
  stepIds: string[];
}

/** Server policy outcome for a step's safety screening (contract API 9). */
export type SkinAnalysisSafetyStatus = 'not_applicable' | 'safe_alternative' | 'safety_unavailable';

/** What a validation fact asserts (contract API 9 `ValidationFactKind`). */
export type SkinAnalysisValidationFactKind =
  | 'metric_match'
  | 'routine_fit'
  | 'skin_type_match'
  | 'ingredient_match'
  | 'external_evidence'
  | 'avoidance_filter';

/** Where a piece of supporting evidence came from (contract API 9 `EvidenceSource`). */
export type SkinAnalysisEvidenceSource =
  | 'skin_analysis_metric'
  | 'routine_policy'
  | 'vertex_candidate'
  | 'deterministic_fallback';

/** One customer-safe fact about why a product passed, was filtered on, or needs care. */
export interface SkinAnalysisValidationFact {
  kind: SkinAnalysisValidationFactKind;
  label: string;
  description?: string;
}

/**
 * Server policy outcomes for a product (contract API 9). `passed` holds facts that ACTUALLY
 * passed — an absent `external_evidence` fact must never become a positive external-evidence
 * claim, and an internal failure reason must never be rendered.
 */
export interface SkinAnalysisValidationFacts {
  passed: SkinAnalysisValidationFact[];
  filtersApplied: SkinAnalysisValidationFact[];
  cautions: SkinAnalysisValidationFact[];
}

/** A public reference backing the pick. Required but MAY be empty — empty means "none to show". */
export interface SkinAnalysisEvidenceReference {
  source: SkinAnalysisEvidenceSource;
  referenceId: string;
  label: string;
  confidence: number;
}

/**
 * What a `whyPicked` support bullet asserts (contract API 9 `EvidenceBulletKind`).
 *
 * `verified` is an objective fact about the product itself; `fit` ties that fact to THIS user's
 * measured, survey, environment or routine context. The spec forbids rendering a `fit` bullet as
 * an objective product claim, so the two never share an icon.
 */
export type SkinAnalysisEvidenceBulletKind = 'verified' | 'fit';

/**
 * A support bullet under the pick (contract API 9). `text` is customer-facing copy the server
 * composed — never invented, reworded or merged on the client.
 */
export interface SkinAnalysisEvidenceBullet {
  kind: SkinAnalysisEvidenceBulletKind;
  text: string;
}

/** Aggregate rating (contract API 9). Absent → the rating row is omitted, never defaulted. */
export interface SkinAnalysisRatingSummary {
  average: number;
  reviewCount: number;
}

/**
 * One representative review highlight with the reviewer profile behind it — the tags are what
 * make "Mentions of people like me" read as relevant rather than generic.
 *
 * Absence means no review projection was published for this result. It does NOT mean the product
 * has no reviews, so nothing may be inferred from it.
 *
 * The backend confirmed (2026-08-12) that this is now the whole shape. `kind` is gone, along with
 * the source language, the translation flag and the "Translated" marker — so there is no longer a
 * provenance to branch on, and the client must not infer one. `usageDurationMonths` is gone too:
 * the duration already arrives inside `tags` as its own entry, e.g. `'6 months'`.
 */
export interface SkinAnalysisReviewHighlight {
  excerpt: string;
  /**
   * The reviewer profile, in the server's own order and wording — traits and how long they used
   * it, e.g. `['Oily', 'acne-prone', '6 months']`. The client only joins them.
   *
   * The server also sends `profileTags` carrying the same values in the same order, kept purely
   * for older clients. Reading one field is enough; reading both would only invite them to
   * disagree.
   */
  tags: string[];
}

/**
 * Product in a routine step. Per the contract, products use the existing
 * SkinSeoulProduct (`SimpleProduct`) model plus the recommendation-only fields below — so this
 * aliases the canonical entity rather than re-declaring a subset (the contract's JSON
 * example is abbreviated). `fitScore` is a deterministic 0–100 score from Facade and is for
 * DISPLAY only: products and their order are already selected by the server.
 */
export type SkinAnalysisRecommendedProduct = SimpleProduct & {
  fitScore: number;
  /** Required rationale — the fallback whenever no `whyPickedVariants` tone is selected. */
  whyPicked: string;
  whyPickedVariants?: Array<{ tone: SkinAnalysisWhyPickedTone; text: string }>;
  /** Server-authored non-clinical expert perspective. Shown only when returned, never synthesized. */
  expertNote?: string;
  ratingSummary?: SkinAnalysisRatingSummary;
  reviewHighlight?: SkinAnalysisReviewHighlight;
  /** Per-ingredient INCI evidence — absent when no metric-relevant ingredient was found. */
  ingredientEvidence?: SkinAnalysisProductIngredientEvidence[];
  /** The ✓/+ bullets supporting `whyPicked`. Absent means there are none to show. */
  evidenceBullets?: SkinAnalysisEvidenceBullet[];
  validationFacts?: SkinAnalysisValidationFacts;
  evidenceReferences: SkinAnalysisEvidenceReference[];
};

/** How a concentration figure should be read (contract API 9 `IngredientConcentration`). */
export type SkinAnalysisIngredientConcentrationQualifier = 'exact' | 'range' | 'at_least' | 'at_most';

/** Where a concentration figure came from. Each carries a different strength of claim. */
export type SkinAnalysisIngredientConcentrationBasis = 'source_claim' | 'inci_order_anchor' | 'regulatory_limit';

/**
 * A sourced concentration claim. `displayText` is the ONLY thing the client prints — the
 * numeric bounds exist so the server can be audited, not so the client can recompute or
 * reformat them. The spec is explicit: render `displayText` as returned, never reinterpret
 * INCI order or calculate percentages.
 *
 * The qualifier decides which bounds are present: `exact` and `range` carry both, `at_least`
 * only a minimum, `at_most` only a maximum — a one-sided claim is never widened to `exact`.
 */
export type SkinAnalysisIngredientConcentration = {
  displayText: string;
  basis: SkinAnalysisIngredientConcentrationBasis;
  confidence: 'verified' | 'bounded';
  /** External claim reference, or the product INCI reference plus its numeric-anchor claim. */
  sourceReference: string;
  policyVersion: string;
} & (
  | { qualifier: 'exact'; minimumPercent: number; maximumPercent: number }
  | { qualifier: 'range'; minimumPercent: number; maximumPercent: number }
  | { qualifier: 'at_least'; minimumPercent: number; maximumPercent?: never }
  | { qualifier: 'at_most'; maximumPercent: number; minimumPercent?: never }
);

/**
 * Evidence for one metric-relevant ingredient found in the product's INCI (contract API 9
 * `ingredientEvidence`). Present only when such an ingredient was found, and `concentration`
 * may still be absent even then.
 *
 * `referencePoint` is a regulatory or functional reference — NOT the product's actual formula —
 * so it may only ever be shown as the labelled reference the server describes.
 */
export interface SkinAnalysisProductIngredientEvidence {
  ingredientName: string;
  ingredientFamily?: string;
  concentration?: SkinAnalysisIngredientConcentration;
  referencePoint?: {
    percent: number;
    label: string;
    sourceReference: string;
    policyVersion: string;
  };
  comparisonText?: string;
  /** Customer-facing sentences. Hide the line when the field or the entry is absent. */
  proofPoints: Array<{ kind: 'active_benefit' | 'formulation_fit'; text: string }>;
}

/**
 * An active ingredient the routine applies to a concern, and the step that carries it.
 * `stepId` resolves against `steps[].stepId` to derive the AM/PM badge and step position —
 * the schedule is NOT repeated here.
 *
 * `description` and `concentration` are both optional and are never derived on the client:
 * the server sends them only when source-grounded text exists.
 */
export interface SkinAnalysisActiveMatch {
  ingredients: Array<{ name: string; concentration?: SkinAnalysisIngredientConcentration }>;
  stepId: string;
  description?: string;
}

/**
 * A prioritised concern the routine was built around (contract API 9 `priorityConcerns`).
 * Array order IS the customer-facing priority order. It may include survey-only concerns
 * such as dryness, so it must never be replaced with the three lowest report metrics.
 */
/**
 * Policy guidance for a concern (contract API 9 `recommendedActives`).
 *
 * Explicitly NOT a claim that any selected product contains the ingredient — that claim is
 * `activeMatches`, which is backed by source ingredient evidence. The contract requires both to
 * be rendered, so an empty `activeMatches` is a normal state, not missing data.
 */
export interface SkinAnalysisRecommendedActive {
  name: string;
  reason: string;
}

export interface SkinAnalysisPriorityConcern {
  concernKey: string;
  label: string;
  source: 'analysis_metric' | 'survey';
  relatedMetricKey?: SkinAnalysisMetricKey;
  description?: string;
  /**
   * Remote icon for this concern — the same server-hosted glyph as `metrics[].iconUrl`, resolved
   * for survey-only concerns too. Optional: responses predating the icons are still in flight,
   * and the slot keeps its placeholder square until one arrives.
   */
  iconUrl?: string;
  /**
   * Ordered code-owned AM/PM active names (SSD-6340) — legacy concerns predating this field
   * reuse stored recommended-active names only, so an empty array is a real, handled state.
   */
  keyIngredients: string[];
  /**
   * Policy guidance for the concern. Required, and EMPTY is the meaningful absence — the
   * contract requires it be shown next to `activeMatches` even when no product matched.
   */
  recommendedActives: SkinAnalysisRecommendedActive[];
  /**
   * Selected-product matches backed by source ingredient evidence. Required, and an empty
   * array means "nothing was verified" rather than missing data.
   */
  activeMatches: SkinAnalysisActiveMatch[];
}

export interface SkinAnalysisRoutineStep {
  stepId: string;
  stepName: string;
  metricKey?: SkinAnalysisMetricKey;
  /** Per-step instruction copy ("how to use") when the backend provides it. */
  description?: string;
  /** Which schedule(s) this step belongs to — e.g. ['AM'], ['PM'], or ['AM','PM']. */
  scheduleTags: SkinAnalysisRoutineScheduleTag[];
  routinePhase?: SkinAnalysisRoutinePhase;
  routineUsage?: SkinAnalysisRoutineUsage;
  /** Position within each schedule, when the server orders a step differently AM vs PM. */
  orderBySchedule?: Partial<Record<SkinAnalysisRoutineScheduleTag, number>>;
  safetyStatus?: SkinAnalysisSafetyStatus;
  hardAvoidLabels?: string[];
  /** Up to 3 products per step. */
  products: SkinAnalysisRecommendedProduct[];
}

/**
 * A single recommended routine (contract API 9). The backend returns ONE routine whose
 * steps each carry their own `scheduleTags` (AM/PM), not separate AM/PM routine blocks.
 * `steps` order is authoritative — filter by schedule tag, never re-sort.
 */
export interface SkinAnalysisRecommendations {
  resultId: string;
  status: 'completed';
  routineName: string;
  routineSummary?: string;
  /** Array order IS the customer-facing priority. Required; empty when none was derived. */
  priorityConcerns: SkinAnalysisPriorityConcern[];
  /**
   * The supply period the routine is priced over — it divides the unique-product total to give
   * the approximate daily cost.
   *
   * Required as of the backend's 2026-08-12 contract: every builder sets it from one
   * routine-level constant, so there is no response left that omits it. The bag still guards on
   * the value being positive, which is a division check rather than a presence one.
   */
  estimatedUsageDays: number;
  steps: SkinAnalysisRoutineStep[];
  /**
   * The canonical AM/PM application order. Absent only on legacy responses, which are the one
   * case where `steps` may be filtered by `scheduleTags` instead.
   */
  routineSequences?: SkinAnalysisRoutineSequence[];
}

// ----- Live recommendation products (SSD-6340) -----
//
// `GET /results/{resultId}/recommendationProducts` and its per-occurrence detail. Unlike the
// `steps[].products[]` above — a snapshot taken when the result was built — these are re-read from
// the catalog on every GET: name, images, SKU, price, discount, stock and rating are all current.
// The contract is explicit that the snapshot may no longer be used for price or stock, so any
// screen that prints a price or offers an add-to-bag reads it from here.

/**
 * Current catalog facts for one stored recommendation occurrence.
 *
 * Addressed by `recommendationItemId`, the occurrence's own UUID: the same product recommended in
 * two steps is two occurrences, each with its own id and its own detail. Only purchasable
 * occurrences are listed — an unpublished, sold-out or invalid-price one is omitted from its step
 * rather than flagged, and the server neither refills nor re-ranks around the gap.
 */
export interface SkinAnalysisRecommendationProductSummary {
  recommendationItemId: string;
  productId: number;
  scheduleTags: SkinAnalysisRoutineScheduleTag[];
  type: 'Simple' | 'Variable';
  status: 'instock';
  currency: SimpleProduct['currency'];
  sku: string;
  name: string;
  brand: string;
  productLink: string;
  productImageUrls: string[];
  category: string[];
  modifiedAt: string;
  size?: string;
  pack?: string;
  originalPrice: number;
  /** Present only while a discount applies. There is no discount-percentage field to read. */
  salePrice?: number;
  ratingSummary?: SkinAnalysisRatingSummary;
  /** Source-grounded active names for this occurrence; `[]` for a result recorded before this contract. */
  keyIngredients: string[];
}

/** Stored step context with only its currently available occurrences attached. */
export interface SkinAnalysisRecommendationProductStep {
  stepId: string;
  stepKey: string;
  stepName: string;
  scheduleTags: SkinAnalysisRoutineScheduleTag[];
  routinePhase?: SkinAnalysisRoutinePhase;
  routineUsage?: SkinAnalysisRoutineUsage;
  orderBySchedule?: Partial<Record<SkinAnalysisRoutineScheduleTag, number>>;
  /**
   * The server's own order, never re-sorted: up to three per AM or PM step, and for a merged
   * `ALL` step the deduplicated union interleaved AM#1, PM#1, AM#2, PM#2, AM#3, PM#3 — up to six.
   * A result recorded before this contract carries exactly the one stored winner.
   */
  products: SkinAnalysisRecommendationProductSummary[];
}

/** The list carries no LLM copy and no review — those belong to the detail. */
export interface SkinAnalysisRecommendationProducts {
  resultId: string;
  status: 'completed';
  steps: SkinAnalysisRecommendationProductStep[];
  routineSequences?: SkinAnalysisRoutineSequence[];
}

/** The stored, product-specific copy for one occurrence — immutable, unlike the product facts beside it. */
export interface SkinAnalysisRecommendationProductCopy {
  whyPicked: string;
  whyPickedVariants?: Array<{ tone: SkinAnalysisWhyPickedTone; text: string }>;
  expertNote?: string;
  evidenceBullets?: SkinAnalysisEvidenceBullet[];
  validationFacts?: SkinAnalysisValidationFacts;
  ingredientEvidence?: SkinAnalysisProductIngredientEvidence[];
}

/**
 * One occurrence in full: current product facts plus its stored copy and at most one review.
 *
 * The price to print is THIS response's — it can differ from the list's by the time the sheet
 * opens, and the contract says the detail's wins.
 */
export interface SkinAnalysisRecommendationProductDetail {
  resultId: string;
  recommendationItemId: string;
  step: {
    stepId: string;
    stepKey: string;
    stepName: string;
    scheduleTags: SkinAnalysisRoutineScheduleTag[];
  };
  product: SkinAnalysisRecommendationProductSummary;
  copy: SkinAnalysisRecommendationProductCopy;
  review: SkinAnalysisReviewHighlight | null;
}

// ----- GET /skinAnalysis/me/overview — Skin Diary entry page -----

/** Where the customer stands against the two-scan limit; drives the re-analyze prompt. */
export interface SkinAnalysisOverviewScan {
  usedCount: number;
  maxCount: number;
  canReanalyze: boolean;
  activeResult: SkinAnalysisResultSummary | null;
}

/** Lowest and highest trait score in the result; the entry page prints it as a range. */
export interface SkinAnalysisOverviewScoreRange {
  min: number;
  max: number;
}

/** One bar in the trait balance chart, in catalog display order. */
export interface SkinAnalysisOverviewTrait {
  metricKey: SkinAnalysisMetricKey;
  label: string;
  score: number;
  /** Null when the survey carried no supported age answer, so no benchmark could be applied. */
  relativeStatus: SkinAnalysisMetricRelativeStatus | null;
}

/** How the nine traits split across the relative buckets; drives the "3 of 9 traits" pill. */
export interface SkinAnalysisOverviewDistribution {
  focusArea: number;
  onPar: number;
  ahead: number;
}

/** The age-band facts a focus-area card prints; always present on a focus area. */
export interface SkinAnalysisOverviewComparison {
  ageBandLabel: string;
  benchmarkScore: number;
  deltaPoints: number;
}

/** A trait scoring below its age-band benchmark, shown as a card on the entry page. */
export interface SkinAnalysisOverviewFocusArea {
  metricKey: SkinAnalysisMetricKey;
  label: string;
  /**
   * The trait's own glyph, hosted by the server — the same icon catalog as `metrics[].iconUrl`,
   * resolved from the metric key on every read. This is what the card draws.
   */
  iconUrl: string;
  /**
   * Kebab-case Lucide name (`circle-dot`, `scan`, `moon`, …), from the contract that predated the
   * catalog and had the client own the nine icons.
   *
   * @deprecated Superseded by `iconUrl`. The server still sends it; nothing renders it.
   */
  iconName: string;
  score: number;
  comparison: SkinAnalysisOverviewComparison;
}

/** A routine step as the entry-page preview renders it, with only the selected product. */
export interface SkinAnalysisOverviewRoutineStep {
  /** One-based position within the full routine, so the preview keeps real step numbers. */
  order: number;
  stepId: string;
  stepName: string;
  productId: number | null;
  productName: string | null;
  productImageUrl: string | null;
  scheduleTags: SkinAnalysisRoutineScheduleTag[];
}

export interface SkinAnalysisOverviewSkinProfile {
  resultId: string;
  completedAt: string;
  traitCount: number;
  scoreRange: SkinAnalysisOverviewScoreRange;
  /** Null when the survey carried no supported age answer. */
  ageBand: SkinAnalysisAgeBandSummary | null;
  traitBalance: SkinAnalysisOverviewTrait[];
  distribution: SkinAnalysisOverviewDistribution;
  focusAreas: SkinAnalysisOverviewFocusArea[];
}

export interface SkinAnalysisOverviewRoutine {
  resultId: string;
  routineName: string;
  totalStepCount: number;
  morningStepCount: number;
  nightStepCount: number;
  /** Already sliced to the first five steps server-side; never the whole routine. */
  previewSteps: SkinAnalysisOverviewRoutineStep[];
}

/** One survey answer rendered as a chip on the About My Skin card. */
export interface SkinAnalysisOverviewSurveyChip {
  value: string;
  label: string;
}

/**
 * About My Skin card: the slice of the stored survey shown back to the customer.
 *
 * The backend projects it from the submitted response on every read — resolving the headline copy,
 * the age label, and every chip label itself — so the client renders these strings verbatim and
 * never maps a question id or reinterprets an option value.
 */
export interface SkinAnalysisOverviewAboutMySkin {
  surveyResponseId: string;
  surveyVersion: string;
  /** Latest report time, falling back to the survey submission until the first scan completes. */
  updatedAt: string;
  /** Already prefixed, e.g. `Ages 25-34` or `Over 60`. Null when the survey carried no age answer. */
  ageLabel: string | null;
  /** Skin type paired with sensitivity, e.g. `Dry & Sensitive` — or just `Dry` when not sensitive. */
  title: string;
  description: string;
  /**
   * The skin type's own glyph, hosted by the server — the same icon catalog as
   * `focusAreas[].iconUrl`, resolved from the type on every read. Null for a survey response whose
   * type the catalog has no artwork for.
   */
  imgUrl: string | null;
  /** Null for survey versions that never asked for a priority. */
  priorityConcern: SkinAnalysisOverviewSurveyChip | null;
  /** Includes an explicit `No preferences` chip when that is what the customer answered. */
  prefers: SkinAnalysisOverviewSurveyChip[];
  /** Includes an explicit `No / Not sure` chip when that is what the customer answered. */
  avoids: SkinAnalysisOverviewSurveyChip[];
}

/**
 * Single call behind the Skin Diary entry page.
 *
 * The backend precomputes this when a result completes, so the page prints a dozen numbers without
 * reading the full report and recommendation snapshots. `skinProfile` and `routine` carry the
 * latest COMPLETED scan even while a newer one is processing or has failed, and are both null until
 * the first scan completes — a user with no completed scan still gets `scan` quota back.
 */
export interface SkinAnalysisMyOverview {
  status: SkinAnalysisStatus;
  scan: SkinAnalysisOverviewScan;
  skinProfile: SkinAnalysisOverviewSkinProfile | null;
  routine: SkinAnalysisOverviewRoutine | null;
  /** Null before the survey is submitted, and for versions that never asked the questions it shows. */
  aboutMySkin: SkinAnalysisOverviewAboutMySkin | null;
}
